import React, { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';

/**
 * CircularTimer — bespoke SVG countdown ring for an SCA challenge.
 *
 * No DS equivalent exists (PRD § 5 / § 11 O-3): the Flowable `SCA_F003`
 * `cloud-html-component` was an SVG + CSS block. Reimplemented to match the
 * original exactly (measured on the live app): a 60 px ring, `r=26`,
 * `strokeWidth=4`, butt linecaps, a grey track and a coloured progress arc
 * (green → amber → red as time runs out), with MM:SS in the centre. Ticks
 * once per second.
 *
 * Colours are 100% theme-driven (no hardcoded hex): the track is
 * `general_dividers`, the MM:SS text is `general_font_primary`, and the
 * traffic-light arc / expired text use the `feedback_notification_*_icon`
 * semantic tokens (`success`/`warning`/`alert` → green/amber/red), which are
 * present in every tenant theme.
 */

// Fraction-remaining thresholds for the arc colour.
const WARN_BELOW = 0.5;
const DANGER_BELOW = 0.2;

const RADIUS = 26;
const VIEWBOX = 60;

export type CircularTimerProps = {
  /** Seconds left when this snapshot was fetched. */
  timeRemaining: number;
  /** Total challenge window, seconds — denominator for the arc fraction. */
  timeFull: number;
  /** Outer diameter in px (defaults to the original 60 px). */
  size?: number;
  strokeWidth?: number;
  /** Fired once when the local countdown reaches zero. */
  onExpire?: () => void;
};

const Wrap = styled.div<{ $size: number }>`
  position: relative;
  display: inline-flex;
  flex: none;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
`;

const Label = styled.span<{ $color?: string }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ $color }) => $color};
`;

const formatMmSs = (totalSeconds: number): string => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
};

export const CircularTimer: React.FC<CircularTimerProps> = ({
  timeRemaining,
  timeFull,
  size = VIEWBOX,
  strokeWidth = 4,
  onExpire,
}) => {
  const theme = useTheme();
  const colors = (theme?.colors ?? {}) as Record<string, string | undefined>;
  const status = (key: 'success' | 'warning' | 'danger'): string | undefined =>
    colors[`feedback_notification_${key === 'danger' ? 'alert' : key}_icon`];
  const trackColor = colors.general_dividers;
  const textColorDefault = colors.general_font_primary;

  const [remaining, setRemaining] = useState(timeRemaining);
  const expiredRef = useRef(false);

  // Re-sync to a fresh server snapshot (e.g. the 30 s list refresh).
  useEffect(() => {
    setRemaining(timeRemaining);
    if (timeRemaining > 0) expiredRef.current = false;
  }, [timeRemaining]);

  // Tick once per second. Functional update keeps the interval value-stable.
  useEffect(() => {
    const id = setInterval(() => {
      setRemaining((r) => (r <= 0 ? 0 : r - 1));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (remaining <= 0 && !expiredRef.current) {
      expiredRef.current = true;
      onExpire?.();
    }
  }, [remaining, onExpire]);

  const fraction =
    timeFull > 0 ? Math.max(0, Math.min(1, remaining / timeFull)) : 0;
  const expired = remaining <= 0;
  const arcColor = expired
    ? trackColor
    : fraction < DANGER_BELOW
      ? status('danger')
      : fraction < WARN_BELOW
        ? status('warning')
        : status('success');
  const textColor = expired ? status('danger') : textColorDefault;

  const circumference = 2 * Math.PI * RADIUS;
  const dashOffset = circumference * (1 - fraction);
  const center = VIEWBOX / 2;

  return (
    <Wrap $size={size} aria-label={formatMmSs(remaining)} role="timer">
      <svg width={size} height={size} viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}>
        <circle
          cx={center}
          cy={center}
          r={RADIUS}
          fill="none"
          stroke={trackColor}
          strokeWidth={strokeWidth}
        />
        {!expired && (
          <circle
            cx={center}
            cy={center}
            r={RADIUS}
            fill="none"
            stroke={arcColor}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            // Start at 12 o'clock (rotate -90) and deplete clockwise. The SVG
            // circle path sweeps clockwise from its start, which makes the arc
            // recede counter-clockwise; reflecting it about the vertical axis
            // (translate + scaleX(-1)) flips the sweep so it empties clockwise
            // while keeping the start at the top.
            transform={`translate(${VIEWBOX} 0) scale(-1 1) rotate(-90 ${center} ${center})`}
          />
        )}
      </svg>
      <Label $color={textColor}>{formatMmSs(remaining)}</Label>
    </Wrap>
  );
};
